import { useCallback, useEffect, useState } from "react";
import { Link, NavLink, useLocation } from "react-router-dom";
import { Menu, X } from "lucide-react";
import { NAV_SECTIONS, SIMPLE_LINKS } from "../../lib/navigation";
import { Logo } from "./Logo";
import { MegaMenu } from "./MegaMenu";

export const Header = () => {
  const [openKey, setOpenKey] = useState(null);
  const [mobileOpen, setMobileOpen] = useState(false);
  const [scrolled, setScrolled] = useState(false);
  const [mobileSection, setMobileSection] = useState(null);
  const location = useLocation();

  const handleScroll = useCallback(() => {
    setScrolled(window.scrollY > 12);
  }, []);

  useEffect(() => {
    handleScroll();
    window.addEventListener("scroll", handleScroll, { passive: true });
    return () => window.removeEventListener("scroll", handleScroll);
  }, [handleScroll]);

  useEffect(() => {
    setOpenKey(null);
    setMobileOpen(false);
    setMobileSection(null);
  }, [location.pathname]);

  const closeMega = useCallback(() => setOpenKey(null), []);

  const activeSection = NAV_SECTIONS.find((s) => s.key === openKey) || null;

  const isHome = location.pathname === "/";
  const headerVisible = !isHome || scrolled || openKey || mobileOpen;

  return (
    <header
      className={`fixed top-0 left-0 right-0 z-50 transition-all duration-500 ${
        headerVisible
          ? "bg-white/95 backdrop-blur-xl border-b border-stone opacity-100 pointer-events-auto translate-y-0"
          : "bg-transparent border-b border-transparent opacity-0 pointer-events-none -translate-y-2"
      }`}
      data-testid="site-header"
      onMouseLeave={closeMega}
      aria-hidden={!headerVisible}
    >
      <div className="container-rozier flex items-center justify-between h-[72px] md:h-[88px]">
        <Logo variant={scrolled || openKey || location.pathname !== "/" ? "dark" : "dark"} />

        <nav className="hidden lg:flex items-center gap-10" aria-label="Navigation principale">
          {SIMPLE_LINKS.map((link) => (
            <NavLink
              key={link.path}
              to={link.path}
              end={link.path === "/"}
              className={({ isActive }) =>
                `text-xs uppercase tracking-[0.28em] transition-colors duration-300 ${
                  isActive ? "text-gold" : "text-charcoal hover:text-gold"
                }`
              }
              data-testid={`nav-link-${link.path.replace(/\//g, "") || "home"}`}
            >
              {link.label}
            </NavLink>
          ))}

          {NAV_SECTIONS.map((section) => (
            <Link
              key={section.key}
              to={section.path}
              onMouseEnter={() => setOpenKey(section.key)}
              onFocus={() => setOpenKey(section.key)}
              className={`text-xs uppercase tracking-[0.28em] transition-colors duration-300 ${
                openKey === section.key || location.pathname.startsWith(section.path)
                  ? "text-gold"
                  : "text-charcoal hover:text-gold"
              }`}
              data-testid={`nav-trigger-${section.key}`}
              aria-haspopup="true"
              aria-expanded={openKey === section.key}
            >
              {section.label}
            </Link>
          ))}
        </nav>

        <div className="hidden lg:block">
          <Link
            to="/contact"
            className="text-xs uppercase tracking-[0.28em] px-5 py-3 border border-charcoal text-charcoal hover:bg-charcoal hover:text-white transition-colors duration-300"
            data-testid="header-cta-contact"
          >
            Prendre contact
          </Link>
        </div>

        <button
          type="button"
          className="lg:hidden text-charcoal"
          onClick={() => setMobileOpen((v) => !v)}
          aria-label={mobileOpen ? "Fermer le menu" : "Ouvrir le menu"}
          data-testid="mobile-menu-toggle"
        >
          {mobileOpen ? <X size={24} /> : <Menu size={24} />}
        </button>
      </div>

      {/* Desktop mega menu */}
      {activeSection && <MegaMenu section={activeSection} onClose={closeMega} />}

      {/* Mobile menu */}
      {mobileOpen && (
        <div className="lg:hidden bg-white border-t border-stone max-h-[calc(100vh-72px)] overflow-y-auto" data-testid="mobile-menu">
          <div className="container-rozier py-6 flex flex-col">
            {SIMPLE_LINKS.map((link) => (
              <Link
                key={link.path}
                to={link.path}
                className="py-4 border-b border-stone text-charcoal text-sm uppercase tracking-[0.28em]"
                data-testid={`mobile-link-${link.path.replace(/\//g, "") || "home"}`}
              >
                {link.label}
              </Link>
            ))}

            {NAV_SECTIONS.map((section) => (
              <div key={section.key} className="border-b border-stone">
                <button
                  type="button"
                  onClick={() =>
                    setMobileSection(mobileSection === section.key ? null : section.key)
                  }
                  className="w-full flex items-center justify-between py-4 text-charcoal text-sm uppercase tracking-[0.28em]"
                  data-testid={`mobile-trigger-${section.key}`}
                >
                  <span>{section.label}</span>
                  <span className="text-gold text-lg leading-none">
                    {mobileSection === section.key ? "−" : "+"}
                  </span>
                </button>
                {mobileSection === section.key && (
                  <div className="pb-4 pl-2">
                    <Link
                      to={section.path}
                      className="block py-2 text-sm text-gold font-serif italic"
                      data-testid={`mobile-section-overview-${section.key}`}
                    >
                      Vue d'ensemble
                    </Link>
                    {section.items.map((item) => (
                      <Link
                        key={item.key}
                        to={item.path}
                        className="block py-2 text-sm text-graphite hover:text-gold transition-colors"
                        data-testid={`mobile-item-${item.key}`}
                      >
                        {item.label}
                      </Link>
                    ))}
                  </div>
                )}
              </div>
            ))}
          </div>
        </div>
      )}
    </header>
  );
};

export default Header;
